import {LazyQueryExecFunction} from '@apollo/client';
import {EventHint, addBreadcrumb} from '@sentry/react';
import {FormikErrors, FormikTouched} from 'formik';
import {round} from 'lodash';
import {OptionsObject, SnackbarKey, SnackbarMessage} from 'notistack';
import React from 'react';
import OWNERSHIP from '../../../../constants/Ownership';
import {createUpdateProps} from '../../useSaveRecommendation/useSaveRecommendation';
import {
  RequirementPots,
  RequirementPotsVariables,
  RequirementPots_destinations,
  RequirementPots_sources,
} from '../../../../schema-types/RequirementPots';
import {shortenName} from '../../../../utils/shortenName';
import {recommendFieldsType} from '../recommendFields';
import {sanitiseProductType} from '../../../../utils/sanitiseProductType';
import {AccountBasicFragment} from '../../../../schema-types/AccountBasicFragment';
import {
  listErrorMessagesAsNodes,
  formatRecommendNestedKeys,
  getListOfErrors,
} from '../recommendValidation';
import {SaltusAny} from '../../../../utils/SaltusAny';
import saltus from '../../../../shared/logger';
import {uuidRegex} from '../../../../components/PrecursorForm/uuidRegex';
import validateUUID from '../../../../utils/validateUUID';

export const hasWrapper = (mainPlanType?: string): boolean => {
  if (!mainPlanType) {
    return false;
  }
  return mainPlanType === 'pension' || mainPlanType === 'bond';
};

export function optionalRound(val?: number | string, multiplier = 1) {
  if (val && typeof val === 'number') {
    return round(val * multiplier, 0);
  }
  try {
    const valNum = Number(val);
    if (Number.isNaN(valNum)) {
      return '';
    }
    return String(round(valNum * multiplier, 2));
  } catch (err) {
    // do nothing
  }
  if (val) {
    return val;
  }
  return '';
}

export const updatePlanTypeContainerSources = ({
  values,
  getSourcesAndDestinations: getSources,
  account,
  accountId,
  recommendReviewId,
}: {
  accountId?: string;
  recommendReviewId?: string;
  values: recommendFieldsType;
  getSourcesAndDestinations: LazyQueryExecFunction<
    RequirementPots,
    RequirementPotsVariables
  >;
  account: AccountBasicFragment | undefined;
}) => {
  // no need for sources or destinations
  if (!values.mainPlanType) {
    return;
  }

  const contactId =
    values.contact && uuidRegex.test(values.contact) ? values.contact : null;
  const variables: RequirementPotsVariables = {
    accountId: account?.id || accountId,
    contactId: validateUUID(contactId),
    potType: sanitiseProductType(values.mainPlanType),
    planAction: values.planAction,
    reviewId: validateUUID(recommendReviewId),
    curoSync: false,
  };
  getSources({
    variables,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });
};

export const setContactnameFromGoals = ({
  values,
  account: accountContactsData,
  setContactName,
}: {
  values: recommendFieldsType;
  // accountContactsData: GetGoalContacts | undefined;
  account: AccountBasicFragment | undefined;
  setContactName: React.Dispatch<React.SetStateAction<string>>;
}) => {
  if (!values.contact) {
    return;
  }
  if (!accountContactsData) {
    return;
  }

  if (accountContactsData.isTrust) {
    setContactName(`${accountContactsData.name}`);
    return;
  }

  if (values.contact === OWNERSHIP.JOINT_CODE && !accountContactsData?.secondaryContact) {
    return;
  }

  if (values.contact === OWNERSHIP.JOINT_CODE) {
    const primary = accountContactsData?.primaryContact?.simplifiedName;
    const secondary = accountContactsData?.secondaryContact?.simplifiedName;
    setContactName(`${primary} & ${secondary}`);
    return;
  }

  const contact =
    // eslint-disable-next-line no-nested-ternary
    accountContactsData.primaryContact?.id === values.contact
      ? accountContactsData.primaryContact
      : accountContactsData.secondaryContact?.id === values.contact
      ? accountContactsData.secondaryContact
      : accountContactsData.contacts?.find((x) => x?.id && x?.id === values.contact);
  // const contact = accountContactsData?.goalContacts.find((x) => x?.id === values.contact);
  setContactName(shortenName(contact?.simplifiedName || contact?.name || '') || '');
};

export const saveDraftRecommendFactory = ({
  setSavingDraft,
  createUpdate,
  values,
  contactName,
  sources,
  destinations,
}: {
  setSavingDraft: React.Dispatch<React.SetStateAction<boolean>>;
  createUpdate: (props: createUpdateProps) => Promise<any>;
  values: recommendFieldsType;
  contactName: string;
  sources: RequirementPots_sources[];
  destinations: RequirementPots_destinations[];
}) => {
  return () => {
    setSavingDraft(true);
    saltus.log({'Saving draft values': values});
    createUpdate({
      values,
      contactName,
      sourcePots: sources,
      destinationPots: destinations,
    } as createUpdateProps).finally(() => {
      setSavingDraft(false);
    });
  };
};

export const finaliseRecommendFactory = ({
  createUpdate,
  values,
  contactName,
  sources,
  destinations,
  setFinalising,
  captureEvent,
  closeSnackbar,
  validateForm,
  setNestedObjectValues,
  setTouched,
  enqueueSnackbar,
  closeWarning,
  refetch,
}: {
  setFinalising: React.Dispatch<React.SetStateAction<boolean>>;
  closeWarning: () => void;
  createUpdate: (props: createUpdateProps) => Promise<any>;
  values: recommendFieldsType;
  contactName: string;
  sources: RequirementPots_sources[];
  destinations: RequirementPots_destinations[];
  captureEvent: (event: Event | any, hint?: EventHint | undefined) => string;
  closeSnackbar: (key?: SnackbarKey | undefined) => void;
  validateForm: (values?: SaltusAny) => Promise<FormikErrors<recommendFieldsType>>;
  setNestedObjectValues: <T>(
    object: SaltusAny,
    value: SaltusAny,
    visited?: SaltusAny,
    response?: SaltusAny
  ) => T;
  setTouched: (
    touched: FormikTouched<recommendFieldsType>,
    shouldValidate?: boolean | undefined
  ) => void;
  enqueueSnackbar: (
    message: SnackbarMessage,
    options?: OptionsObject | undefined
  ) => SnackbarKey;
  refetch: () => void;
}) => {
  return () => {
    addBreadcrumb({
      message: 'Started finalising instruction with form values',
      data: values,
    });
    setFinalising(true);
    closeWarning();
    closeSnackbar();
    validateForm().then((errors) => {
      // set all touched
      const newtouched = setNestedObjectValues({...errors}, true);
      setTouched(newtouched as any);
      setTimeout(async () => {
        if (!errors || Object.keys(errors).length === 0) {
          saltus.log({'Finalising values': values});
          addBreadcrumb({
            message: 'Finalising validated instruction with form values',
            data: values,
          });
          await createUpdate({
            values,
            contactName,
            sourcePots: sources,
            destinationPots: destinations,
            shouldFinalise: true,
          } as createUpdateProps);
          refetch();
          // never turn off finalising if successful to keep it looking tidy
        } else {
          enqueueSnackbar(
            listErrorMessagesAsNodes(getListOfErrors(errors, formatRecommendNestedKeys)),
            {
              variant: 'error',
              autoHideDuration: null,
            }
          );
          addBreadcrumb({
            message: 'instruction validation failed',
            data: {values, errors},
          });

          setFinalising(false);
        }
      });
    });
  };
};
